import React from 'react'
import { ReflectionTrack } from './ReflectionTrack.jsx'
import { SubgoalPickerCard } from './SubgoalPickerCard.jsx'
import { FreeTextCard } from './FreeTextCard.jsx'

const TAGS = ['FACT', 'CAUSE', 'ACTION', 'LEARNING']

const CARDS = [
  { key: 'card1', title: 'What didn\'t land?', placeholder: 'Pick the sub-goals that didn\'t land.', notePlaceholder: 'Anything else that fell short?' },
  { key: 'card2', title: 'What blocked you?', placeholder: 'What got in the way? External or internal?' },
  { key: 'card3', title: 'How did you respond?', placeholder: 'What did you try? What worked, what didn\'t?' },
  { key: 'card4', title: 'What would you do differently?', placeholder: 'If you could rewind, what would you change?' },
]

export function ChallengesTrack({
  data = {},
  onChange,
  managerNotes = {},
  onManagerNoteChange,
  createSuggestHandler,
  subgoals = [],
  snapshots = null,
  isEmployee,
  isManager,
  isShared,
  isReady,
  isClosed,
  employeeName,
}) {
  const readOnly = isClosed || !isEmployee

  const eligibleSubgoals = subgoals.filter((sg) => sg.status !== 'done')
  const selectedIds = data.card1?.subgoalIds || []
  const selectedSet = new Set(selectedIds)
  const allPickerSubgoals = [
    ...eligibleSubgoals,
    ...subgoals
      .filter((sg) => selectedSet.has(sg.subgoalId) && sg.status === 'done')
      .map((sg) => ({ ...sg, _statusChanged: true })),
  ]
  const seen = new Set()
  const dedupedSubgoals = allPickerSubgoals.filter((sg) => {
    if (seen.has(sg.subgoalId)) return false
    seen.add(sg.subgoalId)
    return true
  })

  function countFilled() {
    let count = 0
    if (selectedIds.length > 0 || data.card1?.note) count++
    if (data.card2?.text) count++
    if (data.card3?.text) count++
    if (data.card4?.text) count++
    return count
  }

  function handleCardChange(cardKey, field, value) {
    const current = data[cardKey] || {}
    onChange?.({ ...data, [cardKey]: { ...current, [field]: value } })
  }

  function handleToggleSubgoal(subgoalId) {
    const current = data.card1?.subgoalIds || []
    const next = current.includes(subgoalId)
      ? current.filter((id) => id !== subgoalId)
      : [...current, subgoalId]
    handleCardChange('card1', 'subgoalIds', next)
  }

  return (
    <ReflectionTrack
      title="Challenges"
      filledCount={countFilled()}
      sectionNote={managerNotes.section || ''}
      onSectionNoteChange={(v) => onManagerNoteChange?.({ ...managerNotes, section: v })}
      onSuggestSectionNote={createSuggestHandler?.('challenges', 'section')}
      isManager={isManager}
      isShared={isShared}
      isReady={isReady}
      isClosed={isClosed}
      employeeName={employeeName}
    >
      <SubgoalPickerCard
        tag={TAGS[0]}
        title={CARDS[0].title}
        subgoals={dedupedSubgoals}
        selectedIds={selectedIds}
        onToggle={handleToggleSubgoal}
        note={data.card1?.note || ''}
        onNoteChange={(v) => handleCardChange('card1', 'note', v)}
        placeholder={CARDS[0].placeholder}
        notePlaceholder={CARDS[0].notePlaceholder}
        snapshots={isClosed ? snapshots : null}
        readOnly={readOnly}
        isClosed={isClosed}
        managerNote={managerNotes.card1 || ''}
        onManagerNoteChange={(v) => onManagerNoteChange?.({ ...managerNotes, card1: v })}
        onSuggestManagerNote={createSuggestHandler?.('challenges', 'card1')}
        isManager={isManager}
        isReady={isReady}
        isShared={isShared}
      />

      {CARDS.slice(1).map((card, i) => (
        <FreeTextCard
          key={card.key}
          tag={TAGS[i + 1]}
          title={card.title}
          value={data[card.key]?.text || ''}
          onChange={(v) => handleCardChange(card.key, 'text', v)}
          placeholder={card.placeholder}
          readOnly={readOnly}
          isClosed={isClosed}
          managerNote={managerNotes[card.key] || ''}
          onManagerNoteChange={(v) => onManagerNoteChange?.({ ...managerNotes, [card.key]: v })}
          onSuggestManagerNote={createSuggestHandler?.('challenges', card.key)}
          isManager={isManager}
          isReady={isReady}
          isShared={isShared}
        />
      ))}
    </ReflectionTrack>
  )
}
